import React, { memo, useContext } from 'react'
import { ThemeContext } from 'styled-components'
import Image from '../Image/Image'
import Roundel from '../../helpers/Roundel/Roundel'
import {
  StyledContainer,
  StyledImageContainer,
  StyledBody,
  StyledBodyCopy,
  StyledLogoImage,
  StyledHeading,
  StyledLegalCopy
} from './ImageCtaCard.styles'
import { ImageCtaProps } from './types'
import { createPOIparam } from '../Image/utilities'

const ImageCtaCard = ({
  imageStyle,
  textColor,
  altText,
  logoAltText,
  heading,
  logoImage,
  bodyCopy,
  mainImage,
  dataTest = 'ImageCtaCard',
  backgroundColor,
  textAlignment = 'center',
  isOnlyComponentInGrid = false,
  consumingGridType,
  roundel,
  citrus,
  legalCopy
}: ImageCtaProps): React.ReactElement => {
  const theme = useContext(ThemeContext)
  const roundelBackground = roundel?.type === 'new' ? theme.color.content.new : theme.color.content.sale
  const tabletBreakpointMax = theme.size.breakpoint['max-width'].md
  const tabletBreakpointMin = theme.size.breakpoint['min-width'].sm

  const gridFlex = imageStyle === 'square' ? { block: 'xs' } : { stack: 'xs' }
  const gridTabletRatio = !isOnlyComponentInGrid && consumingGridType === 'ug001' ? '5:4' : undefined
  const imageRatios = {
    square: '1:1',
    short: isOnlyComponentInGrid ? '4:1' : '2:1',
    normal: isOnlyComponentInGrid ? '4:1' : '2:1',
    portrait: '4:5',
    icon: '3:1'
  }

  // Additional media for tablet when in-grid
  const additionalMedia = gridTabletRatio
    ? [
        {
          query: `(min-width: ${tabletBreakpointMin}) and (max-width: ${tabletBreakpointMax})`,
          templates: [createPOIparam(gridTabletRatio)]
        }
      ]
    : undefined

  return (
    <StyledContainer data-test={`${dataTest}-container`} {...gridFlex} gutterValue={0}>
      <StyledImageContainer
        data-test={`${dataTest}-image-container`}
        imageStyle={imageStyle}
        imageRatio={imageRatios[imageStyle]}
        gridTabletRatio={gridTabletRatio || imageRatios[imageStyle]}
        container={dataTest}
        citrus={citrus}
        backgroundColor={backgroundColor}
      >
        {!!mainImage && (
          <Image
            alt={altText}
            data-test={`${dataTest}-main-image`}
            src={mainImage}
            templates={imageStyle !== 'icon' && [createPOIparam(imageRatios[imageStyle])]}
            width={600}
            additionalMedia={additionalMedia}
            smart
          />
        )}
        {roundel && Object.keys(roundel).length > 0 && (
          <Roundel component='c004' roundel={roundel} backgroundColor={roundelBackground} />
        )}
      </StyledImageContainer>

      <StyledBody
        data-test={`${dataTest}-text-container`}
        stack='xs'
        hasLogo={!!logoImage}
        hasSubheading={!!bodyCopy}
        textAlignment={textAlignment}
      >
        {heading && (
          <StyledHeading data-test={`${dataTest}-heading`} textColor={textColor} isOffer={roundel?.type === 'offer'}>
            {heading}
          </StyledHeading>
        )}
        {bodyCopy && <StyledBodyCopy data-test={`${dataTest}-body-copy`}>{bodyCopy}</StyledBodyCopy>}
        {logoImage && (
          <StyledLogoImage data-test={`${dataTest}-logo-image-container`} backgroundColor={backgroundColor}>
            <Image alt={logoAltText} data-test={`${dataTest}-image`} src={logoImage} responsive />
          </StyledLogoImage>
        )}
      </StyledBody>
      {legalCopy && <StyledLegalCopy data-test={`${dataTest}-legal-copy`}>{legalCopy}</StyledLegalCopy>}
    </StyledContainer>
  )
}

export default memo(ImageCtaCard)
